/* eslint-disable max-params, no-param-reassign -- these linting errors come up constantly with reducers */

import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";

import type { GroupClassSeriesAnalyticsSources } from "@/constants/analytics/Analytics";
import type { RootState } from "@/store";
import { useAppDispatch } from "@/store/hooks";
import { extraReducersFactory } from "@/store/reducers/reset";

interface GroupClassDetailsData {
  forWhom?: string;
  groupClassExternalId: string;
  // the specific registered section the tapped card represents; lets the details screen
  // scope to that section instead of blindly picking the first registered one. Undefined
  // for available/recommended cards (one row per group, section chosen on the details screen).
  groupSectionId?: string;
  isAppUserSupport: boolean;
  isDisabled?: boolean;
  isRecommended?: boolean;
  patientId: string;
  // the surface the user came from. Registration happens on the details screen, so the
  // entry point's source has to ride along in order for the register/unregister events
  // fired there to report where the user actually started from.
  source?: GroupClassSeriesAnalyticsSources;
}

interface GroupClassDetailsModalSliceState {
  groupClassDetailsData?: GroupClassDetailsData;
}

const initialState: GroupClassDetailsModalSliceState = {
  groupClassDetailsData: undefined,
};

const groupClassDetailsModalSlice = createSlice({
  // NOTE: extraReducers is required for resetAllReduxSlices to work as expected
  extraReducers: extraReducersFactory(initialState),
  initialState,
  name: "groupClassDetailsModal",
  reducers: {
    resetGroupClassDetailsData: (state) => {
      state.groupClassDetailsData = undefined;
    },
    setGroupClassDetailsData: (
      state,
      action: PayloadAction<GroupClassDetailsData>,
    ) => {
      state.groupClassDetailsData = action.payload;
    },
  },
});

const {
  resetGroupClassDetailsData: resetGroupClassDetailsDataAction,
  setGroupClassDetailsData: setGroupClassDetailsDataAction,
} = groupClassDetailsModalSlice.actions;

const selectGroupClassDetailsData = (state: RootState) =>
  state.groupClassDetailsModal.groupClassDetailsData;

const useGroupClassDetailsModalDispatch = () => {
  const dispatch = useAppDispatch();

  const resetGroupClassDetailsData = () => {
    dispatch(resetGroupClassDetailsDataAction());
  };

  const setGroupClassDetailsData = (data: GroupClassDetailsData) => {
    dispatch(setGroupClassDetailsDataAction(data));
  };

  return {
    resetGroupClassDetailsData,
    setGroupClassDetailsData,
  };
};

export { selectGroupClassDetailsData, useGroupClassDetailsModalDispatch };
export default groupClassDetailsModalSlice.reducer;
